import { supabase } from './supabase';

/**
 * «Дай пропуск, которым правда можно ходить прямо сейчас».
 *
 * 🪤 Урок AssistentusApp (23.08.2026): пропуск нельзя брать из зеркала в
 * браузере — оно отстаёт от настоящего входа, и заголовок уходит ПУСТЫМ, а
 * человек видит «не авторизован» сразу после успешного входа. Пропуск также
 * живёт около часа. Поэтому берём его у самой библиотеки входа: она знает
 * живую сессию и умеет продлить её сама. Продлеваем заранее.
 */
const REFRESH_SKEW_SECONDS = 120;

let inFlight: Promise<string | null> | null = null;

async function resolve(force: boolean): Promise<string | null> {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    let active = session;
    const now = Math.floor(Date.now() / 1000);
    const expiresAt = active?.expires_at ?? 0;
    const stale = force || !active?.access_token || expiresAt - now <= REFRESH_SKEW_SECONDS;

    // Нет сессии вовсе — продлевать нечего, не дёргаем сервер зря.
    if (stale && active) {
      const { data, error } = await supabase.auth.refreshSession();
      if (!error && data.session) active = data.session;
    }

    const token = active?.access_token ?? null;
    const live = !!token && (active?.expires_at ?? 0) - Math.floor(Date.now() / 1000) > 0;
    return live ? token : null;
  } catch {
    // Связь мигнула — решать, что делать с отсутствием пропуска, будет вызывающий.
    return null;
  }
}

/** Живой пропуск или null. Одновременные вызовы сводятся в один запрос. */
export function getFreshAccessToken(force = false): Promise<string | null> {
  if (!inFlight) {
    inFlight = resolve(force).finally(() => { inFlight = null; });
  }
  return inFlight;
}

/** Готовый заголовок. Пустой объект, если пропуска нет — сервер ответит честным 401. */
export async function authHeader(extra?: Record<string, string>): Promise<Record<string, string>> {
  const token = await getFreshAccessToken();
  const h: Record<string, string> = { ...(extra ?? {}) };
  if (token) h['Authorization'] = `Bearer ${token}`;
  return h;
}
